import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Briefcase,
  AlertOctagon,
  Activity,
  GitBranch,
  Building2,
  Clock,
  ChevronRight,
  Filter,
  Settings,
  Mail,
  Inbox,
  Archive
} from 'lucide-react';

// Iconos por categoría
const CATEGORY_ICONS = {
  alertas: AlertTriangle,
  casos: Briefcase,
  escalaciones: AlertOctagon,
  monitores: Activity,
  aprobaciones: GitBranch,
  onboarding: Building2,
  sistema: Bell
};

// Colores por categoría
const CATEGORY_COLORS = {
  alertas: 'bg-amber-50 text-amber-700 border-amber-200',
  casos: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  escalaciones: 'bg-red-50 text-red-700 border-red-200',
  monitores: 'bg-purple-50 text-purple-700 border-purple-200',
  aprobaciones: 'bg-blue-50 text-blue-700 border-blue-200',
  onboarding: 'bg-green-50 text-green-700 border-green-200',
  sistema: 'bg-slate-50 text-slate-700 border-slate-200'
};

// Colores por prioridad
const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700'
};

const PRIORITY_LABELS = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica'
};

const NotificacionesInbox = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [categories, setCategories] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadNotifications();
    loadCategories();
  }, []);

  const loadNotifications = async (unreadOnly = false, category = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (unreadOnly) params.append('unread_only', 'true');
      if (category && category !== 'all') params.append('category', category);
      params.append('limit', '100');
      
      const [notifRes, countRes] = await Promise.all([
        api.get(`/notifications/v2/inbox?${params.toString()}`),
        api.get('/notifications/v2/inbox/count')
      ]);
      
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get('/notifications/v2/categories');
      setCategories(res.data || {});
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/v2/inbox/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true, read_at: new Date().toISOString()} : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Error al marcar como leída');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const res = await api.post('/notifications/v2/inbox/read-all');
      toast.success(`${res.data.marked_count} notificaciones marcadas como leídas`);
      setNotifications(prev => 
        prev.map(n => ({...n, read: true, read_at: new Date().toISOString()}))
      );
      setUnreadCount(0);
    } catch (error) {
      toast.error('Error al marcar todas como leídas');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/notifications/v2/inbox/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notificación eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadNotifications(tab === 'unread', categoryFilter !== 'all' ? categoryFilter : null);
  };

  const handleCategoryChange = (category) => {
    setCategoryFilter(category);
    loadNotifications(activeTab === 'unread', category !== 'all' ? category : null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
  };

  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'unread' && n.read) return false;
    if (categoryFilter !== 'all' && n.category !== categoryFilter) return false;
    return true;
  });

  const isAdmin = user?.rol?.toUpperCase() === 'ADMIN' || 
                  user?.rol?.toUpperCase() === 'SUPERADMIN' || 
                  user?.rol?.toUpperCase() === 'ADMINISTRADOR';

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="text-indigo-600" />
            Mis Notificaciones
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white ml-2">{unreadCount}</Badge>
            )}
          </h1>
          <p className="text-slate-600 mt-1">
            Gestiona tus notificaciones y alertas del sistema
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => navigate('/m/notificaciones/admin')}
            >
              <Settings size={16} className="mr-2" />
              Administrar
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => loadNotifications(activeTab === 'unread')}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck size={16} className="mr-2" />
              Marcar todas leídas
            </Button>
          )}
        </div>
      </div>

      {/* Tabs y Filtros */}
      <Card className="bg-white mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="all" className="flex items-center gap-1">
                  <Inbox size={14} />
                  Todas
                </TabsTrigger>
                <TabsTrigger value="unread" className="flex items-center gap-1">
                  <Bell size={14} />
                  No leídas
                  {unreadCount > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">{unreadCount}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-slate-400" />
              <Select value={categoryFilter} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px]" style={{ backgroundColor: '#fff' }}>
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  <SelectItem value="alertas">Alertas</SelectItem>
                  <SelectItem value="casos">Casos</SelectItem>
                  <SelectItem value="escalaciones">Escalaciones</SelectItem>
                  <SelectItem value="monitores">Monitores</SelectItem>
                  <SelectItem value="aprobaciones">Aprobaciones</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Notificaciones */}
      <Card className="bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-slate-400" size={24} />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <BellOff size={48} className="mb-3" />
              <p>No hay notificaciones</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredNotifications.map((notification) => {
                const CategoryIcon = CATEGORY_ICONS[notification.category] || Bell;
                const categoryColor = CATEGORY_COLORS[notification.category] || CATEGORY_COLORS.sistema;
                const priorityColor = PRIORITY_COLORS[notification.priority] || PRIORITY_COLORS.medium;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-slate-50 transition-colors ${!notification.read ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icono */}
                      <div className={`p-2 rounded-lg ${categoryColor} border`}>
                        <CategoryIcon size={18} />
                      </div>
                      
                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                                {notification.title}
                              </h4>
                              {!notification.read && (
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-xs capitalize">
                                {notification.category}
                              </Badge>
                              <Badge className={`text-xs ${priorityColor}`}>
                                {PRIORITY_LABELS[notification.priority] || notification.priority}
                              </Badge>
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Clock size={12} />
                                {formatDate(notification.created_at)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Acciones */}
                          <div className="flex items-center gap-1">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkAsRead(notification.id)}
                                title="Marcar como leída"
                              >
                                <Check size={16} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(notification.id)}
                              className="text-slate-400 hover:text-red-500"
                              title="Eliminar"
                            >
                              <Trash2 size={16} />
                            </Button>
                            {notification.link && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  handleMarkAsRead(notification.id);
                                  navigate(notification.link);
                                }}
                                title="Ver detalle"
                              >
                                <ChevronRight size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificacionesInbox;
